import { getInitials, IContextualMenuItem, PersonaSize } from '@fluentui/react';
import { History } from 'history';

import { MenuItem } from '../../components/AppBar/AppBar';
import { personaStylesMenuButton, personaStylesThick } from '../../components/Persona/Persona.styles';
import { navigate } from '../../utils/utils';

export enum AppStoreMenuName {
  about = 'About',
  home = 'Home',
  appStore = 'App Store',
  myApps = 'My Apps',
  myModels = 'My Models',
  myInstances = 'My Instances',
  myAiEngines = 'My AI Engines',
  platformUsage = 'Platform Usage',
  collaborate = 'Collaborate',
  featureStore = 'Feature Store',
  adminMarketplaceApps = 'Admin Marketplace Apps',
  adminApps = 'Admin Apps',
  adminInstances = 'Admin Instances',
  adminAiEngines = 'Admin AI Engines',
  aiEngineSettings = 'AI Engines Settings',
  notebookAdminSettings = 'Notebook Settings',
  documentation = 'Documentation',
  visitorMode = 'Visitor Mode',
  cliApiAccess = 'CLI & API Access',
  apiAccess = 'API Access',
  license = 'License',
  logout = 'Logout',
}

// Backward-compat enum kept for downstream projects still passing AppName.
export enum AppName {
  appStore = 'appStore',
}

export interface AppUser {
  name?: string;
  id?: string;
  isAdmin?: boolean;
  isSuperAdmin?: boolean;
  hasFullAccess?: boolean;
  visitorModeEnabled?: boolean;
  picture?: string;
  isPublic?: boolean;
}

export interface FeatureStoreMenuInfo {
  url?: string;
  hasMenu?: boolean;
}

export interface AppStoreMenuInfo {
  user?: AppUser;
  externalMenuItems?: MenuItem[];
  bookTimeLink?: string;
  hasHomePage?: boolean;
  hasAiEngineManager?: boolean;
  hasMyApp?: boolean;
  hasPlatformUsage?: boolean;
  hasBookTime?: boolean;
  url?: string;
  hasMenu?: boolean;
  hasAdminMarketPlaceApps?: boolean;
  hasAdminMenus?: boolean;
  hasSuperAdminMenus?: boolean;
  hasVisitorMode?: boolean;
  isPublicMode?: boolean;
  isVisitorMode?: boolean;
  environmentName?: string;
  hasNotebook?: boolean;
  cliDownloadEnabled?: boolean;
}

export const getAppStoreMenuInfo = (
  hasAiEngineManager?: boolean,
  user?: AppUser,
  navLinks: { label: string; url: string }[] = [],
  bookTimeLink = 'https://h2o.ai',
  hasHomePage = false,
  federationEnabled = false,
  url?: string,
  hasMenu?: boolean,
  environmentName?: string,
  hasPlatformUsage?: boolean,
  isPublicMode = false,
  hasNotebook = false,
  cliDownloadEnabled = false
): AppStoreMenuInfo => {
  const { hasFullAccess, visitorModeEnabled, isAdmin, isSuperAdmin } = user || {};
  const isVisitor = visitorModeEnabled || (!isAdmin && !hasFullAccess);
  const hasBookTime = isVisitor && !!bookTimeLink;
  const externalMenuItems = convertNavLinksToMenuItems(navLinks);

  return {
    hasAiEngineManager,
    user,
    externalMenuItems,
    bookTimeLink,
    hasBookTime,
    hasHomePage,
    hasMyApp: hasBookTime || (hasFullAccess && !visitorModeEnabled),
    hasAdminMarketPlaceApps: isAdmin && !visitorModeEnabled && federationEnabled,
    hasAdminMenus: isAdmin && !visitorModeEnabled,
    hasSuperAdminMenus: isSuperAdmin && !visitorModeEnabled,
    hasVisitorMode: hasFullAccess,
    isPublicMode,
    isVisitorMode: visitorModeEnabled,
    url,
    hasMenu,
    environmentName,
    hasPlatformUsage,
    hasNotebook,
    cliDownloadEnabled,
  };
};

const relInfo = 'noopener noreferrer';

export const convertNavLinksToMenuItems = (
  navLinks: { label: string; url: string }[] = [],
  _appName: AppName = AppName.appStore
): MenuItem[] => {
  return navLinks.map(({ label, url }) => {
    return {
      key: label,
      dataTest: `${label.replace(' ', '-').toLowerCase()}--main-menu-item`,
      name: label,
      to: url,
      isExternal: true,
      rel: relInfo,
    };
  });
};

export const getAppStoreMenus = ({
  hasMenu,
  url = '',
  hasHomePage,
  hasMyApp,
  hasAiEngineManager,
  externalMenuItems = [],
  hasBookTime,
}: AppStoreMenuInfo = {}) => {
  if (!hasMenu) {
    return [];
  }
  const menus: MenuItem[] = [];
  const isExternal = !!url;
  const rel = isExternal ? relInfo : '';
  if (hasHomePage) {
    menus.push({
      key: AppStoreMenuName.home,
      dataTest: 'home--main-menu-item',
      name: AppStoreMenuName.home,
      to: `${url}/home`,
      isExternal,
      rel,
    });
  }
  menus.push({
    key: AppStoreMenuName.appStore,
    dataTest: 'app-store--main-menu-item',
    name: AppStoreMenuName.appStore,
    to: `${url}/appstore`,
    isExternal,
    rel,
  });
  if (hasMyApp) {
    menus.push({
      key: AppStoreMenuName.myApps,
      dataTest: 'my-apps--main-menu-item',
      name: AppStoreMenuName.myApps,
      to: `${url}/myapps`,
      isExternal,
      rel,
    });
  }
  menus.push({
    key: AppStoreMenuName.myInstances,
    dataTest: 'my-instances--main-menu-item',
    name: AppStoreMenuName.myInstances,
    to: `${url}/instances`,
    isExternal,
    rel,
  });
  if (hasAiEngineManager) {
    menus.push({
      key: AppStoreMenuName.myAiEngines,
      dataTest: 'my-ai-engines-menu-item',
      name: AppStoreMenuName.myAiEngines,
      to: `${url}/aiengines`,
      isExternal,
      rel,
    });
  }
  menus.push(...externalMenuItems);
  if (hasBookTime) {
    // this is just a link to open the bookTimeDialog.
    menus.push({
      key: AppStoreMenuName.collaborate,
      dataTest: `book-time--main-menu-item`,
      name: AppStoreMenuName.collaborate,
      to: '/book-time',
      isExternal: true,
    });
  }
  return menus;
};

export const getMenuItems = (appStore?: AppStoreMenuInfo) => {
  const menuItems: MenuItem[] = [];
  if (appStore) {
    menuItems.push(...getAppStoreMenus(appStore));
  }
  return menuItems;
};

export const getAppStoreUserMenus = (
  {
    hasMenu,
    url = '',
    hasAdminMarketPlaceApps,
    hasAdminMenus,
    hasSuperAdminMenus,
    hasVisitorMode,
    hasPlatformUsage,
    isVisitorMode,
    hasAiEngineManager,
    isPublicMode,
    hasNotebook,
    cliDownloadEnabled,
  }: AppStoreMenuInfo,
  history?: History
) => {
  if (!hasMenu) {
    return [];
  }
  const menus: IContextualMenuItem[] = [];
  if (hasAdminMarketPlaceApps) {
    menus.push({
      key: AppStoreMenuName.adminMarketplaceApps,
      text: AppStoreMenuName.adminMarketplaceApps,
      onClick: () => navigateOrAddHistory('/admin/marketplace/apps', history, url),
      'data-test': 'admin-marketplace-apps--user-menu-item',
    });
  }
  if (hasAdminMenus) {
    menus.push(
      {
        key: AppStoreMenuName.adminApps,
        text: AppStoreMenuName.adminApps,
        onClick: () => navigateOrAddHistory('/admin/apps', history, url),
        'data-test': 'admin-apps--user-menu-item',
      },
      {
        key: AppStoreMenuName.adminInstances,
        text: AppStoreMenuName.adminInstances,
        onClick: () => navigateOrAddHistory('/admin/instances', history, url),
        'data-test': 'admin-instances--user-menu-item',
      }
    );
    if (hasAiEngineManager) {
      menus.push({
        key: AppStoreMenuName.adminAiEngines,
        text: AppStoreMenuName.adminAiEngines,
        onClick: () => navigateOrAddHistory('/admin/aiengines', history, url),
        'data-test': 'admin-ai-engines--user-menu-item',
      });
    }
  }
  if (hasSuperAdminMenus && hasAiEngineManager) {
    menus.push({
      key: AppStoreMenuName.aiEngineSettings,
      text: AppStoreMenuName.aiEngineSettings,
      onClick: () => navigateOrAddHistory('/admin/aiengines/settings', history, url),
      'data-test': 'ai-engine-settings--user-menu-item',
    });
  }
  if (hasNotebook && hasSuperAdminMenus) {
    menus.push({
      key: AppStoreMenuName.notebookAdminSettings,
      text: AppStoreMenuName.notebookAdminSettings,
      onClick: () => navigateOrAddHistory('/admin/notebook/settings', history, url),
      'data-test': 'notebook-admin-settings--user-menu-item',
    });
  }
  if (!isPublicMode && hasPlatformUsage) {
    menus.push({
      key: AppStoreMenuName.platformUsage,
      text: AppStoreMenuName.platformUsage,
      'data-test': 'platform-usage--user-menu-item',
      onClick: () => navigateOrAddHistory('/platform-usage', history, url),
    });
  }
  menus.push({
    key: AppStoreMenuName.documentation,
    text: AppStoreMenuName.documentation,
    onClick: () => navigate('https://docs.h2o.ai/haic/'),
    'data-test': 'doc--menu-item',
  });
  if (!isPublicMode) {
    if (hasVisitorMode) {
      const toggleVisitorMode = async (checked: boolean) => {
        await window.fetch(`/v1/visitor-mode?enabled=${checked}`, { method: 'POST' });
        window.location.reload();
      };
      menus.push({
        key: AppStoreMenuName.visitorMode,
        name: AppStoreMenuName.visitorMode,
        canCheck: true,
        isChecked: isVisitorMode,
        onClick: () => {
          toggleVisitorMode(!isVisitorMode);
        },
        'data-test': 'visitor-toggle--user-menu-item',
      });
    }
    menus.push({
      key: cliDownloadEnabled ? AppStoreMenuName.cliApiAccess : AppStoreMenuName.apiAccess,
      text: cliDownloadEnabled ? AppStoreMenuName.cliApiAccess : AppStoreMenuName.apiAccess,
      onClick: () => navigateOrAddHistory('/cli-and-api-access', history, url),
      'data-test': 'cli-and-api-access--menu-item',
    });
  }
  return menus;
};

export const getUserMenuItems = (
  appStore?: AppStoreMenuInfo,
  onLogout?: () => void,
  history?: History,
  onAbout?: () => void
) => {
  const menuItems: IContextualMenuItem[] = [];
  if (appStore) {
    menuItems.push(...getAppStoreUserMenus(appStore, history));
  }
  if (onAbout) {
    menuItems.push({
      key: AppStoreMenuName.about,
      text: AppStoreMenuName.about,
      onClick: onAbout,
      'data-test': 'about--user-menu-item',
    });
  }
  if (onLogout) {
    menuItems.push({
      key: AppStoreMenuName.logout,
      text: AppStoreMenuName.logout,
      onClick: onLogout,
      'data-test': 'logout--user-menu-item',
    });
  }
  return menuItems;
};

const navigateOrAddHistory = (path = '', history?: History, url = '') => {
  const p = `${url}${path}`;
  if (history && !url) {
    history.push(p);
  } else if (url) {
    navigate(p);
  }
};

export const getPersonaProps = ({ user: { name, picture, isAdmin } = {}, environmentName }: AppStoreMenuInfo) => {
  return {
    styles: [personaStylesMenuButton, personaStylesThick],
    imageUrl: picture,
    imageInitials: name ? getInitials(name.split('@')[0].replace(/[^A-Z]/gi, ' '), false) : '',
    text: name,
    size: PersonaSize.size40,
    showOverflowTooltip: false,
    secondaryText: (isAdmin ? 'Administrator' : 'User') + (environmentName ? ` | ${environmentName}` : ''),
  };
};
